'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';

import {
  getPersonalSettingsNavigationItems,
  getWorkspaceSettingsNavigationItems,
  type SettingsNavigationItem,
} from '@/app/[orgSlug]/settings/components/settings-navigation-state';
import { getDrivesCopy } from '@/i18n/drives-copy';
import { getWorkspaceFilesCopy } from '@/i18n/workspace-files-copy';
import { getWorkspaceNavigationCopy } from '@/i18n/workspace-navigation-copy';
import { api, useQuery } from '@/lib/convex';
import { getScheduledEntryCopy } from '@/lib/scheduled/entry-copy';
import { workspaceFilesHref } from '@/lib/workspace-files-route';

export type GoToSettingsEntry = {
  id: string;
  label: string;
  href: string;
  keywords: string[];
};

/** Translated labels and per-viewer links the Go to list needs, gathered
 * from the copy the sidebar, Drive rail and settings already use. */
export type GoToContext = {
  labels: {
    newChat: string;
    workChats: string;
    myDrive: string;
    schedule: string;
    connectors: string;
    downloads: string;
    trash: string;
    support: string;
    pricing: string;
    usage: string;
    mediaLibrary: string;
    productsFolder: string;
    avatarsFolder: string;
    brands: string;
    sharedWithMe: string;
    teams: string;
    projects: string;
  };
  myDriveHref: string;
  settings: GoToSettingsEntry[];
};

// Settings pages behind billing or storage permissions stay out of the menu;
// the pages themselves enforce access, and Pricing/Usage have their own rows.
const MENU_SETTINGS_PERMISSIONS = {
  canManageBilling: false,
  canViewBilling: false,
  canViewStorageLimits: false,
  showFilePermissions: false,
};

// Rows the Go to list already covers on its own.
const DUPLICATE_SETTINGS_SLUGS = new Set(['members']);

export function useGoToContext({
  orgSlug,
  enabled,
}: {
  orgSlug?: string;
  enabled: boolean;
}): GoToContext | undefined {
  const locale = useLocale();
  const shell = useTranslations('WorkspaceShell');
  const settingsT = useTranslations('SettingsShell');
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    enabled && orgSlug ? { slug: orgSlug } : 'skip'
  );
  const workspaceUsername = organization.data?.workspaceUsername;

  return useMemo(() => {
    if (!orgSlug) return undefined;
    const navigation = getWorkspaceNavigationCopy(locale);
    const drives = getDrivesCopy(locale);
    const files = getWorkspaceFilesCopy(locale);
    const settingsLabel = (item: SettingsNavigationItem) =>
      settingsT(item.label);
    const settingsItems = [
      ...getWorkspaceSettingsNavigationItems({
        orgSlug,
        permissions: MENU_SETTINGS_PERMISSIONS,
      }),
      ...getPersonalSettingsNavigationItems(),
    ].filter(
      (item) =>
        !DUPLICATE_SETTINGS_SLUGS.has(item.slug) &&
        item.label !== 'archivedChats' &&
        item.label !== 'filePermissions' &&
        item.label !== 'storage'
    );
    return {
      labels: {
        newChat: shell('newChat'),
        workChats: navigation.inbox,
        myDrive: drives.myDrive,
        schedule: getScheduledEntryCopy(locale).navLabel,
        connectors: shell('connectors'),
        downloads: shell('downloads'),
        trash: shell('trash'),
        support: shell('support'),
        pricing: shell('pricing'),
        usage: shell('usage'),
        mediaLibrary: shell('myLibrary'),
        productsFolder: files.products,
        avatarsFolder: files.avatars,
        brands: files.brands,
        sharedWithMe: drives.sharedWithMe,
        teams: drives.teams,
        projects: drives.projects,
      },
      myDriveHref: workspaceUsername
        ? workspaceFilesHref(orgSlug, `/${orgSlug}/users/@${workspaceUsername}`)
        : `/${orgSlug}/files`,
      settings: settingsItems.map((item) => ({
        id: `settings-${item.href}`,
        label: settingsLabel(item),
        href: item.href,
        keywords: ['settings', item.slug, ...item.searchTerms],
      })),
    };
  }, [locale, orgSlug, settingsT, shell, workspaceUsername]);
}
